// C11 — Configuración de turnos por planta.
//
// 🚧 PENDIENTE refactor visual a detalle (sesión 2026-05-28).
// Ajuste mínimo aplicado para coherencia con C1/C2/C3/C5/C6:
//   - Header consistente (título + subtítulo informativo + botones top-right)
//   - Tokens semánticos (text-fg / text-fg-tertiary / text-fg-secondary, sin colores hardcoded)
//   - Loading/error states alineados con el resto
//   - Filtros (si aplica) en barra horizontal con bg-surface-overlay/40
//   - Cualquier <Table>/<TR>/<TD> custom → tabla raw con clases consistentes
//
// NO se agregó lógica nueva, KPI cards con sparkline ni gráfico Recharts —
// eso queda para la revisión a detalle por pantalla.
//
// Estado original — listado agrupado por planta. Cada planta puede tener 1-4 turnos.
// El flag cruza_medianoche se calcula automáticamente en el backend.

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { TurnoConfig } from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  useCoordinadorTurnosList,
  useCrearTurno,
  useEditarTurno,
  useEliminarTurno,
  useGuardarHorarioSemanaTurno,
  useHorarioSemanaTurno,
} from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { siguienteDiaIso } from '../../lib/siguienteDiaIso';
import { cn } from '../../lib/utils';
import { HorarioPorDiaDialog } from './HorarioPorDiaDialog';

type CrearCtx = { plantaId: string; plantaNombre: string };

export function C11ConfigTurnosPage() {
  const { state } = useAuth();
  const { data, isLoading, isError, error } = useCoordinadorTurnosList();
  const [crearCtx, setCrearCtx] = useState<CrearCtx | null>(null);
  const [editing, setEditing] = useState<TurnoConfig | null>(null);
  const [horarioTurno, setHorarioTurno] = useState<TurnoConfig | null>(null);

  const turnosPorPlanta = useMemo(() => {
    const map = new Map<string, TurnoConfig[]>();
    if (!data?.turnos) return map;
    for (const t of data.turnos) {
      const arr = map.get(t.planta_id) ?? [];
      arr.push(t);
      map.set(t.planta_id, arr);
    }
    return map;
  }, [data?.turnos]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando turnos…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="border-b border-border pb-3">
          <h1 className="text-xl font-medium text-fg">Configuración de turnos</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data.tenant.nombre} · turnos por planta (T1/T2/T3 y opcional T4)
          </p>
        </header>

        {data.recursos.plantas.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-fg-tertiary">
              Sin plantas asignadas a este coordinador.
            </CardContent>
          </Card>
        ) : (
          data.recursos.plantas.map((p) => {
            const turnos = (turnosPorPlanta.get(p.id) ?? []).sort((a, b) => a.numero - b.numero);
            const numerosOcupados = new Set(turnos.map((t) => t.numero));
            const puedeAgregar = numerosOcupados.size < 4;
            return (
              <section key={p.id}>
                <div className="mb-3 flex items-end justify-between">
                  <div>
                    <h2 className="text-sm font-medium text-fg">{p.nombre}</h2>
                    <p className="text-[10px] text-fg-tertiary">
                      Código {p.codigo} · {turnos.length} turno(s)
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!puedeAgregar}
                    onClick={() => setCrearCtx({ plantaId: p.id, plantaNombre: p.nombre })}
                  >
                    Nuevo turno
                  </Button>
                </div>
                {turnos.length === 0 ? (
                  <Card>
                    <CardContent className="py-4 text-center text-sm text-fg-tertiary">
                      Sin turnos configurados.
                    </CardContent>
                  </Card>
                ) : (
                  <Table maxHeight="70vh">
                    <THead sticky>
                      <TR>
                        <TH align="right">N°</TH>
                        <TH>Nombre</TH>
                        <TH align="right">Inicio</TH>
                        <TH align="right">Fin</TH>
                        <TH align="center">Cruza 00:00</TH>
                        <TH align="center">Estado</TH>
                        <TH align="right" />
                      </TR>
                    </THead>
                    <TBody>
                      {turnos.map((t) => (
                        <TR key={t.id}>
                          <TD align="right" numeric className="font-medium text-fg">
                            T{t.numero}
                          </TD>
                          <TD className="text-fg">{t.nombre}</TD>
                          <TD align="right" numeric className="text-fg-secondary">
                            {t.hora_inicio}
                          </TD>
                          <TD align="right" numeric className="text-fg-secondary">
                            {t.hora_fin}
                          </TD>
                          <TD align="center">
                            {t.cruza_medianoche ? (
                              <span className="text-warn">●</span>
                            ) : (
                              <span className="text-fg-tertiary">—</span>
                            )}
                          </TD>
                          <TD align="center">
                            <span
                              className={cn(
                                'inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                                t.activo ? 'bg-ok/10 text-ok' : 'bg-fg/10 text-fg-tertiary',
                              )}
                            >
                              {t.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </TD>
                          <TD align="right">
                            <Button size="sm" variant="ghost" onClick={() => setHorarioTurno(t)}>
                              Horario por día
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>
                              Editar
                            </Button>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </section>
            );
          })
        )}
      </div>

      {crearCtx && (
        <NuevoTurnoDialog
          plantaId={crearCtx.plantaId}
          plantaNombre={crearCtx.plantaNombre}
          numerosOcupados={
            new Set((turnosPorPlanta.get(crearCtx.plantaId) ?? []).map((t) => t.numero))
          }
          onClose={() => setCrearCtx(null)}
        />
      )}
      {editing && <EditarTurnoDialog turno={editing} onClose={() => setEditing(null)} />}
      {horarioTurno && (
        <HorarioPorDiaContainer turno={horarioTurno} onClose={() => setHorarioTurno(null)} />
      )}
    </RoleLayout>
  );
}

// "Horario por día" (R6): contenedor — resuelve GET/PUT para el diálogo presentacional.
// Los días se ordenan desde el inicio de la semana operativa del tenant, y
// aplicarDesdeDefault es el día siguiente al día operativo en curso (/api/me); el
// servidor igual valida y devuelve 400 con la fecha exacta si queda corta.
function HorarioPorDiaContainer({ turno, onClose }: { turno: TurnoConfig; onClose: () => void }) {
  const { data, isLoading, isError } = useHorarioSemanaTurno(turno.id);
  const guardar = useGuardarHorarioSemanaTurno(turno.id);
  const [serverError, setServerError] = useState<string | null>(null);
  const [programacionesAfectadas, setProgramacionesAfectadas] = useState<number | null>(null);
  const { diaInicioSemana, hoyOperativo } = useTenantConfig();
  const aplicarDesdeDefault = siguienteDiaIso(hoyOperativo);

  if (isLoading || isError || !data) {
    return (
      <Dialog open onClose={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Horario por día · {turno.nombre}</DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-fg-secondary">
              <Spinner className="size-5" /> Cargando…
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-crit">
              No pudimos cargar el horario por día.
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <HorarioPorDiaDialog
      turnoNombre={turno.nombre}
      diaInicioSemana={diaInicioSemana}
      dias={data.dias}
      base={{ hora_inicio: turno.hora_inicio, hora_fin: turno.hora_fin }}
      aplicarDesdeDefault={aplicarDesdeDefault}
      guardando={guardar.isPending}
      serverError={serverError}
      programacionesAfectadas={programacionesAfectadas}
      onGuardar={(body) => {
        setServerError(null);
        guardar.mutate(body, {
          onSuccess: (res) => {
            setProgramacionesAfectadas(res.programaciones_afectadas_total);
            toast.success('Horario actualizado');
            onClose();
          },
          onError: (err) => setServerError(err instanceof Error ? err.message : 'Error al guardar'),
        });
      }}
      onClose={onClose}
    />
  );
}

// =============================================================================

function NuevoTurnoDialog({
  plantaId,
  plantaNombre,
  numerosOcupados,
  onClose,
}: {
  plantaId: string;
  plantaNombre: string;
  numerosOcupados: Set<number>;
  onClose: () => void;
}) {
  const crear = useCrearTurno();
  const disponibles = [1, 2, 3, 4].filter((n) => !numerosOcupados.has(n));
  const [form, setForm] = useState({
    numero: disponibles[0] ?? 1,
    nombre: '',
    hora_inicio: '06:00',
    hora_fin: '14:00',
  });

  const submit = async () => {
    if (!form.nombre) {
      toast.error('Ingrese un nombre');
      return;
    }
    try {
      await crear.mutateAsync({
        planta_id: plantaId,
        numero: form.numero,
        nombre: form.nombre,
        hora_inicio: form.hora_inicio,
        hora_fin: form.hora_fin,
      });
      toast.success('Turno creado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al crear');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo turno · {plantaNombre}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="t-numero">Número *</Label>
            <Select
              id="t-numero"
              value={String(form.numero)}
              onChange={(e) => setForm({ ...form, numero: Number(e.target.value) })}
            >
              {disponibles.map((n) => (
                <option key={n} value={n}>
                  T{n}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="t-nombre">Nombre *</Label>
            <Input
              id="t-nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Turno 1 mañana"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="t-inicio">Hora inicio *</Label>
              <Input
                id="t-inicio"
                type="time"
                value={form.hora_inicio}
                onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="t-fin">Hora fin *</Label>
              <Input
                id="t-fin"
                type="time"
                value={form.hora_fin}
                onChange={(e) => setForm({ ...form, hora_fin: e.target.value })}
              />
            </div>
          </div>
          <p className="text-[11px] text-fg-tertiary">
            Si la hora fin es menor que la de inicio, el turno cruza medianoche automáticamente.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={crear.isPending}>
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================

function EditarTurnoDialog({ turno, onClose }: { turno: TurnoConfig; onClose: () => void }) {
  const editar = useEditarTurno(turno.id);
  const eliminar = useEliminarTurno();
  const [form, setForm] = useState({
    nombre: turno.nombre,
    hora_inicio: turno.hora_inicio,
    hora_fin: turno.hora_fin,
    activo: turno.activo,
  });

  const submit = async () => {
    try {
      await editar.mutateAsync({
        nombre: form.nombre,
        hora_inicio: form.hora_inicio,
        hora_fin: form.hora_fin,
        activo: form.activo,
      });
      toast.success('Turno actualizado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al editar');
    }
  };

  const onEliminar = async () => {
    if (!confirm('¿Desactivar este turno?')) return;
    try {
      await eliminar.mutateAsync(turno.id);
      toast.success('Turno desactivado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Editar T{turno.numero} · {turno.planta_nombre}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="te-nombre">Nombre *</Label>
            <Input
              id="te-nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="te-inicio">Hora inicio *</Label>
              <Input
                id="te-inicio"
                type="time"
                value={form.hora_inicio}
                onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="te-fin">Hora fin *</Label>
              <Input
                id="te-fin"
                type="time"
                value={form.hora_fin}
                onChange={(e) => setForm({ ...form, hora_fin: e.target.value })}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })}
              className="size-4"
            />
            Turno activo
          </label>
        </div>
        <DialogFooter>
          {turno.activo && (
            <Button variant="danger" onClick={onEliminar} loading={eliminar.isPending}>
              Desactivar
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={editar.isPending}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
